import React from "react";
import { makeStyles, Theme } from "@material-ui/core/styles";
import CircularProgress from "@material-ui/core/CircularProgress";
import Typography from "@material-ui/core/Typography";
import Skeleton from "@material-ui/lab/Skeleton";
import clsx from "clsx";

import LapiImg from "../../LapiImg";

export type Size = "small" | "medium" | "large";

type Dimensions = {
  fontSize: string;
  height: number;
  thickness: number;
};

type DimensionsBySize = {
  [key in Size]: Dimensions;
};

const SIZE: DimensionsBySize = {
  small: {
    fontSize: "1rem",
    height: 48,
    thickness: 5,
  },
  medium: {
    fontSize: "1.25rem",
    height: 72,
    thickness: 5.5,
  },
  large: {
    fontSize: "1.5rem",
    height: 96,
    thickness: 6,
  },
};

const useStyles = makeStyles<
  Theme,
  Pick<OcrProps, "reliabilityColor"> & { size: Size }
>((theme: Theme) => ({
  /* Styles applied to the root element. */
  root: {
    width: "100%",
    display: "flex",
    flexDirection: "row",
    flexWrap: "wrap",
    justifyContent: "center",
    alignItems: "center",
  },
  ocrLicensePlate: {
    width: ({ size }) => SIZE[size].height * 3,
    height: ({ size }) => SIZE[size].height,
    borderRadius: theme.shape.borderRadius,
    marginBottom: theme.spacing(1),
  },
  rightContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  },
  reliabilityRateContainer: {
    position: "relative",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    marginLeft: 8,
  },
  reliabilityRateCircularProgressBackground: {
    position: "absolute",
    color: theme.palette.grey[300],
    opacity: 0.5,
  },
  reliabilityRateCircularProgress: {
    color: ({ reliabilityColor }) => {
      return reliabilityColor || theme.palette.action.disabled;
    },
  },
  reliabilityRateLabel: {
    position: "absolute",
    fontSize: ({ size }) => SIZE[size].fontSize,
  },
}));

export type OcrProps = React.DetailedHTMLProps<
  React.HTMLAttributes<HTMLDivElement>,
  HTMLDivElement
> & {
  /**
   * The plate image used by the OCR.
   */
  plateImage?: string;
  /**
   * Color to indicate the user the level of confidence of the OCR.
   * (default to `theme.palette.action.disabled`)
   */
  reliabilityColor?: string;
  /**
   * The reliability rate announced by the OCR for the plate image.
   * Must be between 0 and 100 as it is a percentage.
   */
  reliabilityRate?: number;
  /**
   * The size of the OCR component, (default to `medium`)
   */
  size?: Size;
  /**
   * The vehicle id to display
   */
  vehicleId?: string;
};

/**
 * Display the reliability rate announced by the OCR and the plate image
 * used by the OCR. If one of the props plateImage or reliabilityRate is missing,
 * this component will stay in loading state, displaying some ghost loaders.
 * If the reliabilityColor props is not provided, the circle will be grey.
 *
 * @param props - The Ocr component props.
 * @throws Will throw an error if the reliabilityRate is less than 0.
 * @throws Will throw an error if the reliabilityRate is greater than 100.
 */
function Ocr(props: OcrProps) {
  const {
    className,
    plateImage,
    reliabilityColor,
    reliabilityRate,
    size = "medium",
    vehicleId,
    ...other
  } = props;
  const classes = useStyles({ reliabilityColor, size });
  const rootClasses = React.useMemo(() => {
    return clsx(classes.root, className);
  }, [className, classes.root]);
  const loading = React.useMemo(() => {
    return plateImage === undefined || reliabilityRate === undefined;
  }, [plateImage, reliabilityRate]);

  React.useEffect(() => {
    if (reliabilityRate === undefined) return;
    if (0 > reliabilityRate) {
      throw new Error("The reliability rate can't be less than 0");
    }
    if (reliabilityRate > 100) {
      throw new Error("The reliability rate can't be greater than 100");
    }
  }, [reliabilityRate]);

  return (
    <div className={rootClasses} {...other}>
      {loading ? (
        <>
          <Skeleton className={classes.ocrLicensePlate} variant="rect" />
          <div className={classes.reliabilityRateContainer}>
            <Skeleton
              variant="circle"
              width={SIZE[size].height}
              height={SIZE[size].height}
            />
          </div>
        </>
      ) : (
        <>
          <LapiImg
            className={classes.ocrLicensePlate}
            src={plateImage}
            alt="ocr license plate"
          />
          <div className={classes.rightContainer}>
            <div className={classes.reliabilityRateContainer}>
              <CircularProgress
                className={classes.reliabilityRateCircularProgressBackground}
                variant="determinate"
                size={SIZE[size].height}
                thickness={SIZE[size].thickness}
                value={100}
              />
              <CircularProgress
                className={classes.reliabilityRateCircularProgress}
                variant="determinate"
                size={SIZE[size].height}
                thickness={SIZE[size].thickness}
                value={reliabilityRate}
              />
              <Typography
                className={classes.reliabilityRateLabel}
                variant="caption"
              >
                <b>{reliabilityRate}%</b>
              </Typography>
            </div>
            {vehicleId && (
              <Typography>
                LAPI: <b>{vehicleId}</b>
              </Typography>
            )}
          </div>
        </>
      )}
    </div>
  );
}

export default Ocr;
